import { Bot, CheckCircle2 } from 'lucide-react'

interface Step1Props {
  storeName: string
  businessType: string
  targetAudience?: string
  userName?: string
  onStoreNameChange: (value: string) => void
  onBusinessTypeChange: (value: string) => void
  onTargetAudienceChange: (value: string) => void
  onNext: () => void
}

interface CapabilityItem {
  title: string
  detail: string
}

const SECTOR_CAPABILITIES: Record<string, { label: string; items: CapabilityItem[] }> = {
  tienda_repuestos: {
    label: 'Tienda de Repuestos & Autopartes',
    items: [
      { title: 'Original vs. Reemplazo', detail: 'Diferencia marcas originales (OEM) y opciones garantizadas con claridad.' },
      { title: 'Venta cruzada natural', detail: 'Sugiere lubricantes, filtros y arandelas al cotizar autopartes.' },
      { title: 'Cierre de mostrador', detail: 'Confirma de inmediato si retira en tienda o prefiere delivery al taller.' },
    ],
  },
  restaurante: {
    label: 'Restaurante & Gastronomía',
    items: [
      { title: 'Atención en horas pico', detail: 'Toma órdenes completas al instante sin que los clientes esperen.' },
      { title: 'Adicionales y combos', detail: 'Ofrece bebidas, acompañantes y postres para elevar el ticket promedio.' },
      { title: 'Reglas y precios de tu carta', detail: 'Cotiza con las opciones, acompañantes y precios fijados por tu negocio.' },
    ],
  },
  bodegon_licoreria: {
    label: 'Bodegón & Licorería',
    items: [
      { title: 'Sugerencias de ocasión', detail: 'Recomienda combos de licores, snacks y hielo en compras de fin de semana.' },
      { title: 'Despacho express', detail: 'Coordina entregas rápidas y valida disponibilidad antes de cobrar.' },
      { title: 'Reglas de tu negocio', detail: 'Aplica tus métodos de cobro, existencias y condiciones de entrega.' },
    ],
  },
  salud: {
    label: 'Salud, Clínicas & Laboratorios',
    items: [
      { title: 'Requisitos y preparación', detail: 'Responde dudas sobre ayuno, indicaciones de exámenes y ubicación.' },
      { title: 'Pre-agendamiento asistido', detail: 'Registra la solicitud horaria del paciente para confirmación por recepción.' },
      { title: 'Seguridad clínica', detail: 'Cortafuegos estricto: cero diagnósticos automatizados por IA.' },
    ],
  },
  ferreteria: {
    label: 'Ferretería & Construcción',
    items: [
      { title: 'Medidas técnicas', detail: 'Comprende pulgadas, milímetros, sacos y galones con total exactitud.' },
      { title: 'Precios por volumen', detail: 'Aplica escalas de precios para contratistas y maestros de obra.' },
      { title: 'Logística pesada', detail: 'Coordina entregas en flete pesado o retiro directo en patio.' },
    ],
  },
  retail: {
    label: 'Retail & Moda',
    items: [
      { title: 'Catálogo con fotos y tallas', detail: 'Muestra opciones disponibles según color, medida o colección.' },
      { title: 'Envíos y políticas', detail: 'Resuelve dudas de cobertura nacional, plazos y cambios.' },
      { title: 'Carritos listos', detail: 'Agrupa productos y transfiere el pedido al asesor para cobrar.' },
    ],
  },
  belleza_estetica: {
    label: 'Belleza & Estética',
    items: [
      { title: 'Catálogo de servicios', detail: 'Detalla duración, tarifas y especialistas para cada tratamiento.' },
      { title: 'Solicitud de turnos', detail: 'Toma solicitudes de citas para que el personal confirme la agenda.' },
      { title: 'Ubicación y recomendaciones', detail: 'Envía dirección, estacionamiento y pautas previas al turno.' },
    ],
  },
  inmobiliaria: {
    label: 'Bienes Raíces & Inmobiliaria',
    items: [
      { title: 'Filtro por preferencias', detail: 'Identifica zona, rango de precio y características deseadas.' },
      { title: 'Fichas con fotos', detail: 'Comparte detalles y fotos de propiedades activas.' },
      { title: 'Prospectos calificados', detail: 'Transfiere al cliente listo al asesor inmobiliario correspondiente.' },
    ],
  },
  servicios: {
    label: 'Servicios Profesionales',
    items: [
      { title: 'Alcance y propuestas', detail: 'Explica tus servicios clave y resuelve dudas iniciales del cliente.' },
      { title: 'Captura de requerimientos', detail: 'Recopila los datos del proyecto antes de la primera llamada.' },
      { title: 'Handoff comercial', detail: 'Pasa el contacto calificado a tu equipo para cerrar la cotización.' },
    ],
  },
}

const DEFAULT_CAPABILITIES = {
  label: 'Tu negocio en WhatsApp',
  items: [
    { title: 'Atención 24/7 sin menús', detail: 'Responde con fluidez natural sin frustrar al comprador.' },
    { title: 'Cotización con stock en vivo', detail: 'Informa precios y existencias en tiempo real según las reglas de tu tienda.' },
    { title: 'Pase suave a tu equipo', detail: 'Transfiere la orden lista a tus asesores cuando el cliente confirma.' },
  ],
}

export default function Step1BusinessInfo({
  storeName,
  businessType,
  targetAudience,
  onStoreNameChange,
  onBusinessTypeChange,
  onTargetAudienceChange,
  onNext,
}: Step1Props) {
  const currentCapabilities = SECTOR_CAPABILITIES[businessType] || DEFAULT_CAPABILITIES

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-slate-900 text-3xl font-bold tracking-tight mb-2">
          Cuéntanos sobre tu negocio
        </h1>
        <p className="text-slate-600 text-base">
          Personalizaremos las habilidades de tu asistente basándonos en tu tipo de negocio.
        </p>
      </div>

      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); onNext() }}>
        <div className="flex flex-col gap-2">
          <label className="text-slate-900 text-sm font-semibold" htmlFor="shop-name">
            Nombre de la tienda/negocio
          </label>
          <input
            id="shop-name"
            type="text"
            value={storeName}
            onChange={(e) => onStoreNameChange(e.target.value)}
            placeholder="Ej. Mi Tienda Comercial"
            maxLength={80}
            className="w-full h-14 px-4 rounded-xl border border-emerald-900/15 bg-white focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none transition-all placeholder:text-slate-400 text-sm"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-slate-900 text-sm font-semibold" htmlFor="business-type">
            Tipo de negocio
          </label>
          <select
            id="business-type"
            value={businessType}
            onChange={(e) => onBusinessTypeChange(e.target.value)}
            className="w-full h-14 px-4 rounded-xl border border-emerald-900/15 bg-white focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none transition-all text-sm appearance-none"
          >
            <option value="" disabled>Selecciona una opción</option>
            <option value="retail">🛍️ Retail / Tienda de Moda y Calzado</option>
            <option value="tienda_repuestos">🚗 Tienda de Repuestos / Autopartes</option>
            <option value="restaurante">🍽️ Restaurante / Gastronomía y Delivery</option>
            <option value="bodegon_licoreria">🍷 Bodegón y Licorería Express</option>
            <option value="belleza_estetica">💅 Salón de Belleza, Barbería y Spa</option>
            <option value="ferreteria">🔧 Ferretería y Materiales de Construcción</option>
            <option value="inmobiliaria">🏠 Bienes Raíces e Inmobiliaria</option>
            <option value="turismo_posadas">🌴 Posadas, Hoteles y Turismo</option>
            <option value="salud">🏥 Salud, Clínicas y Laboratorios</option>
            <option value="servicios">💼 Servicios Profesionales y Técnicos</option>
            <option value="tecnologia">💻 Tecnología y Electrónica</option>
            <option value="educacion">📚 Educación y Academias</option>
            <option value="otro">✨ Otro tipo de negocio</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-slate-900 text-sm font-semibold" htmlFor="target-audience">
            Audiencia objetivo (Opcional)
          </label>
          <textarea
            id="target-audience"
            value={targetAudience || ''}
            onChange={(e) => onTargetAudienceChange(e.target.value)}
            placeholder="Describe a quién le vendes (ej. Talleres mecánicos y dueños de vehículos particulares)"
            maxLength={120}
            rows={3}
            className="w-full p-4 rounded-xl border border-emerald-900/15 bg-white focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none transition-all placeholder:text-slate-400 text-sm resize-none"
          />
        </div>

        {/* Dynamic Contextual Capabilities Preview */}
        <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/50 p-5 shadow-2xs">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div>
              <span className="text-xs font-bold text-emerald-950 uppercase tracking-wide">
                Lo que tu asistente sabrá hacer automáticamente
              </span>
              <p className="text-[11px] text-emerald-700 font-medium">
                {currentCapabilities.label}
              </p>
            </div>
          </div>

          <div className="space-y-2.5 pt-1">
            {currentCapabilities.items.map((item, idx) => (
              <div key={idx} className="flex items-start gap-2.5 text-xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-800 font-semibold">{item.title}: </strong>
                  <span className="text-slate-600">{item.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            type="submit"
            className="w-full bg-[#075f55] text-white h-14 rounded-xl font-bold text-lg hover:bg-[#075f55]/90 transition-colors shadow-lg shadow-[#075f55]/20 active:scale-[0.98]"
          >
            Siguiente
          </button>
        </div>
      </form>
    </div>
  )
}
